/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Row, Col } from "react-awesome-styled-grid";
import {
  Button,
  Box,
  ModalConfirm,
  Sidebar,
  Text,
  Loader,
} from "@nordcloud/gnui";
import {
  useOrgUnitTypesV2Query,
  useUpdateOrgUnitTypeMutation,
  useAddOrgUnitTypeMutation,
} from "~/generated/graphql";
import {
  Pagination,
  ErrorMessage,
  BreadcrumbsBox,
  ReactTable,
} from "~/components";
import { ERROR_TEXT } from "~/constants";
import { useQueryState } from "~/hooks";
import { useRemoveOrgUnitType } from "~/hooks/graphql";
import { showSuccess, showError } from "~/services/toast";
import { generateActionConfirmText, generateActionSuccessText } from "~/tools";
import { BusinessContextsForm } from "../businessContexts/BusinessContextsForm";
import { OrganizationUnitTypesListColumns } from "./OrganizationUnitTypesListColumns";

type FormValues = NonNullable<
  React.ComponentProps<typeof BusinessContextsForm>["initialValues"]
>;

export function OrganizationUnitTypes() {
  const {
    state: { page, limit },
  } = useQueryState();
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [isModalConfirmOpen, setIsModalConfirmOpen] = React.useState(false);
  const [deleteData, setDeleteData] = React.useState({
    id: "",
    name: "",
  });
  const [initialValues, setInitialValues] = React.useState({
    id: "",
    name: "",
    description: "",
    color: "",
  });

  const {
    data: orgUnitTypesData,
    loading,
    error,
    refetch,
  } = useOrgUnitTypesV2Query({
    variables: { limit, page },
  });

  const [addOrgUnitType, { loading: loadingAdd, error: errorAdd }] =
    useAddOrgUnitTypeMutation();

  const [removeOrgUnitType, { loading: loadingRemove, error: errorRemove }] =
    useRemoveOrgUnitType();

  const [updateOrgUnitType, { loading: loadingUpdate, error: errorUpdate }] =
    useUpdateOrgUnitTypeMutation();

  const onRemoveHandler = React.useCallback(
    async (id: string) => {
      try {
        setIsModalConfirmOpen(false);
        await removeOrgUnitType({ id });
        showSuccess(
          generateActionSuccessText(`The ${deleteData.name}`)()("removed")()
        );
      } catch {
        showError(ERROR_TEXT.default);
      }
    },
    [removeOrgUnitType, deleteData]
  );

  const onEditHandler = React.useCallback(
    (item: FormValues) => {
      setInitialValues({
        id: item.id,
        name: item.name,
        description: item.description !== "-" ? item.description : "",
        color: item.color,
      });
      setIsEditMode(true);
      setIsSidebarOpen(!isSidebarOpen);
    },
    [setInitialValues, setIsSidebarOpen, isSidebarOpen]
  );

  const onUpdate = async (values: FormValues) => {
    const { id, ...input } = values;
    try {
      setIsSidebarOpen(!isSidebarOpen);
      await updateOrgUnitType({ variables: { id, input } });
      showSuccess(
        generateActionSuccessText(`The ${values.name}`)()("updated")()
      );
    } catch {
      showError(ERROR_TEXT.default);
    }
  };

  const onSubmit = async (values: FormValues) => {
    try {
      setIsSidebarOpen(!isSidebarOpen);
      await addOrgUnitType({ variables: { input: values } });
      showSuccess(generateActionSuccessText(`The ${values.name}`)()("added")());
      refetch();
    } catch {
      showError(ERROR_TEXT.default);
    }
  };

  const columns = React.useMemo(
    () =>
      OrganizationUnitTypesListColumns({
        onEditHandler,
        setIsModalConfirmOpen,
        setDeleteData,
      }),
    [onEditHandler, setIsModalConfirmOpen, setDeleteData]
  );

  const types = orgUnitTypesData?.orgUnitTypesV2?.types ?? [];
  const data = types.map((unit) => ({
    ...unit,
    description: unit.description || "-",
  }));

  return (
    <>
      <Row>
        <Col>
          <ErrorMessage
            error={error || errorAdd || errorRemove || errorUpdate}
          />
          <BreadcrumbsBox title="Organization Unit Types">
            <Button
              icon="plus"
              onClick={() => {
                setIsEditMode(false);
                setIsSidebarOpen(!isSidebarOpen);
              }}
            >
              Add New Unit Type
            </Button>
          </BreadcrumbsBox>
          <Box spacing="spacing04">
            <Text weight="medium">List of Organization Unit Types</Text>
            {loading || loadingAdd || loadingRemove || loadingUpdate ? (
              <Loader />
            ) : (
              <ReactTable columns={columns} data={data} />
            )}
            <Pagination count={orgUnitTypesData?.orgUnitTypesV2?.count || 0} />
          </Box>
        </Col>
      </Row>
      <Sidebar
        title={isEditMode ? "Edit Unit Type" : "Add new Unit Type"}
        isOpen={isSidebarOpen}
        onClick={() => {
          setIsSidebarOpen(!isSidebarOpen);
        }}
      >
        <BusinessContextsForm
          isEditMode={isEditMode}
          initialValues={initialValues}
          type="Organization Unit Type"
          hasColor={false}
          onSubmit={onSubmit}
          onUpdate={onUpdate}
        />
      </Sidebar>
      <ModalConfirm
        isOpen={isModalConfirmOpen}
        confirm={() => onRemoveHandler(deleteData.id)}
        actionLabel="Delete"
        contentLabel="Delete Organizational Unit"
        onClose={() => setIsModalConfirmOpen(false)}
      >
        {generateActionConfirmText(deleteData.name)("delete")()}
      </ModalConfirm>
    </>
  );
}
